import { Link } from "react-router-dom";

type Tier = {
  key: string;
  name: string;
  forWho: string;
  model: string;
  modelNote: string;
  features: string[];
  cta: string;
  featured?: boolean;
};

/* Honest beta pricing (founder call 2026-07-02): show the MODEL, not invented
 * numbers. Real rates land when set — CONTENT_MAP G1–G3. */
const TIERS: Tier[] = [
  {
    key: "builder",
    name: "Builder",
    forWho: "Residential & light-commercial builders",
    model: "Per stamped lot",
    modelNote: "pay for permit-ready packages, not seats",
    features: [
      "Reuse one master floor plan across every lot",
      "Lot-specific calcs auto-generated per site",
      "Site hazard data resolved per address (ASCE 7)",
      "Stamped, permit-ready package",
      "Shared access for your engineer of record",
    ],
    cta: "Book a demo",
  },
  {
    key: "firm",
    name: "Firm",
    forWho: "Structural engineering firms",
    model: "Per engineer seat",
    modelNote: "unlimited projects per seat",
    features: [
      "All three control modes (manual / automated / AI)",
      "Design profiles + reusable firm standards",
      "Member database + live autosize across the catalog",
      "Full decision log + exportable calc records",
      "Audit trail across every project",
    ],
    cta: "Book a demo",
    featured: true,
  },
  {
    key: "enterprise",
    name: "Multi-firm",
    forWho: "Consulting PEs & multi-office groups",
    model: "Custom",
    modelNote: "volume & multi-org agreements",
    features: [
      "One account across many firms, fully isolated",
      "Org roles, SSO, and centralized billing",
      "Tenant isolation per firm, enforced rather than configured",
      "Early access to new code editions",
      "Onboarding & a dedicated contact",
    ],
    cta: "Book a demo",
  },
];

export function Pricing() {
  return (
    <section className="landing-section landing-band" id="pricing">
      <div className="landing-wrap landing-wrap--wide">
        <div className="section-head">
          <span className="section-eyebrow">Pricing</span>
          <h2 className="section-title">Simple models. Founding rates in beta.</h2>
          <p className="section-sub">
            Builders pay per stamped lot; firms pay per engineer seat. We&apos;re in private beta:
            rates are being set with our first firms, and beta partners lock in founding pricing.
          </p>
        </div>

        <div className="pricing-grid">
          {TIERS.map((t) => (
            <div className={`price-card${t.featured ? " is-featured" : ""}`} key={t.key}>
              {t.featured && <span className="price-flag">Most firms</span>}
              <p className="price-name">{t.name}</p>
              <p className="price-for">{t.forWho}</p>
              <div className="price-amount">
                <span className="price-model">{t.model}</span>
                <span className="price-unit">{t.modelNote}</span>
              </div>
              <ul className="price-features">
                {t.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <Link to="/beta?intent=demo" className={`btn ${t.featured ? "btn-primary" : "btn-secondary"} price-cta`}>
                {t.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* The done-for-you lane (founder, 2026-07-11): Firmark is run by engineers —
            firms that don't want new software send the plan instead. */}
        <div className="price-service">
          <div className="price-service-copy">
            <h3 className="price-service-title">Or skip the software: send us the plan.</h3>
            <p className="price-service-desc">
              Firmark is run by engineers, and some firms would rather send work than learn a tool.
              Start the intake with your PDF or DWG; a licensed engineer runs it through the same
              engine and hands back a stamp-ready plan set and calc package, typically in one to
              two days.
            </p>
          </div>
          <div className="price-service-side">
            <div className="price-amount">
              <span className="price-model">Per plan set</span>
              <span className="price-unit">quoted at intake · 1–2 day turnaround</span>
            </div>
            <Link to="/beta?intent=plans" className="btn btn-secondary">Send us your plan</Link>
            <Link to="/plans" className="text-link price-service-more">How it works →</Link>
          </div>
        </div>

        <p className="pricing-note">
          Beta partners get founding rates and a direct line to the team building it. Every plan
          keeps the stamp with your licensed PE.
        </p>
      </div>
    </section>
  );
}
